import { HighwaySectionDto } from '@interfaces/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { DaysOfWeekInOrder } from '@helpers/utils';
import { useTranslation } from 'react-i18next';
import { DayOfWeek } from '@helpers/enums';
import { useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { useMemo } from 'react';
import { z } from 'zod';

import { useServicesFormatter } from '../../hooks';

export const highwaysConfigurationFormDefaultValues = {
  highwaySections: [],
  days: [],
};

const highwaysConfigurationFormSchema = (t: TFunction) =>
  z.object({
    highwaySections: z
      .array(z.string())
      .min(1, t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SECTIONS_REQUIRED')),
    days: z
      .array(z.nativeEnum(DayOfWeek))
      .min(1, t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_DAYS_REQUIRED')),
  });

export type HighwaysConfigurationFormData = z.infer<
  ReturnType<typeof highwaysConfigurationFormSchema>
>;

interface UseHighwaysConfigurationFormProps {
  defaultValues: HighwaysConfigurationFormData;
  highwaySections: HighwaySectionDto[];
  days: DayOfWeek[];
}

export const useHighwaysConfigurationForm = (props: UseHighwaysConfigurationFormProps) => {
  const { defaultValues, highwaySections, days } = props;

  const { formatDays, formatHighwaysSections } = useServicesFormatter();
  const { t } = useTranslation();

  const form = useForm({
    resolver: zodResolver(highwaysConfigurationFormSchema(t)),
    defaultValues: defaultValues ?? highwaysConfigurationFormDefaultValues,
    mode: 'onBlur',
  });

  const formSelectedDays = form.watch('days');
  const formSelectedHighwaysSections = form.watch('highwaySections');

  const selectedDaysCustomValue = formatDays(formSelectedDays as DayOfWeek[]);
  const selectedHighwaysSectionsCustomValue = formatHighwaysSections(
    formSelectedHighwaysSections as string[],
    highwaySections,
  );

  const daysOptions = useMemo(() => {
    return days.map((day) => ({
      label: t(`DAYS.${day}`),
      value: day,
    }));
  }, [days, t]);

  const sortDays = (selectedValues: string[]) => {
    return selectedValues.sort(
      (a, b) =>
        DaysOfWeekInOrder.indexOf(a as DayOfWeek) - DaysOfWeekInOrder.indexOf(b as DayOfWeek),
    );
  };

  const sortHighwaysSections = (selectedValues: string[]) => {
    return selectedValues.sort((a: string, b: string) => {
      const sectionA = highwaySections.find((section) => section.code === a);
      const sectionB = highwaySections.find((section) => section.code === b);

      if (sectionA && sectionB) {
        return sectionA.highwayName.localeCompare(sectionB.highwayName);
      }

      return 0;
    });
  };

  const highwaysSectionOptions = useMemo(() => {
    return highwaySections.map((section) => ({
      label: `${section.highwayName}: ${t('COMPANY_POLICY.HIGHWAYS_CONFIGURATION_SECTION')} ${section.sectionName}`,
      value: section.code,
    }));
  }, [highwaySections, t]);

  return {
    selectedHighwaysSectionsCustomValue,
    selectedDaysCustomValue,
    highwaysSectionOptions,
    daysOptions,
    form,
    sortHighwaysSections,
    sortDays,
  };
};
